import {GraphyoConfigEditor} from './GraphyoConfigEditor';
import {GraphyoActivity} from "./GraphyoActivity";
import {ProtocolOrderGame} from './ProtocolOrderGame';
import {ColorimetricTitrationDiagram} from './ColorimetricTitrationDiagram';
import {ChoiceHint} from './ChoiceHint';
import {ChemicalLaboratory} from './ChemicalLaboratory';
import {chemicalLabAction} from './chemical-lab-session';
import {mergeChemicalMeasurements} from './chemical-lab-measurements';
import type {WorldAction} from './mini-lab-mechanics';
import {chemicalLaboratoryId} from './chemical-lab-config';
import {ProgressAnalogy,ReactionProgressTable,StructuredFields} from './ReactionProgress';
import {measurementEngineId} from './measurement-engine';
import {MeasurementEnginePreview,MeasurementAndTitrationEngine} from './MeasurementAndTitrationEngine';
import {MoleculeActivity} from './MoleculeActivity';
import {DocumentMolecules,DocumentMoleculesEditor} from './DocumentMolecules';
import {formatChemistryText} from './chemical-typography';
import {MoleculeReferenceFields} from './MoleculeReferenceFields';
import {ValidatedManipulation} from './ValidatedManipulation';
import {graphSourcePoints} from './graph-source-data';
import {InteractiveGraph,GraphConfigEditor} from './InteractiveGraph';
import {defaultGraphConfig} from './interactive-graph-model';
import { MathText } from "./MathText";
import {PedagogicalText} from './PedagogicalCorrection';
import {CalibrationTable} from './CalibrationTable';
import {CalibrationGraph} from './CalibrationGraph';
import {calibrationRows} from './calibration-data';
import {measurementMode} from './animation-choices';
import {ExperimentFrame} from './ExperimentFrame';
import {applyMeasurementEvent,sourceMeasurements} from './resource-measurements';
import {isExperiment} from './resources/measurement-contract';
import {ResourceFrame} from "./ResourceFrame";
import {resourceForAsset} from "./interactive-resources";
import {ResourceParameters} from "./InteractiveResourceLibrary";
import { ReactNode, useRef, useState } from "react";
import {ResourceMeasurementAnswer} from './ResourceMeasurementAnswer';
import { DopplerYouTube, dopplerYouTubeTrack } from "./DopplerYouTube";
import { CatalogCompetency } from "./catalog";
import {
  InteractiveActivityBlock as Block,
  InteractiveAnswerType,
  InteractiveAssetRef,
  InteractiveQuestionBlock,
} from "./interactive-activity";
import { InteractiveActivityPackage as Package } from "./interactive-activity-package";
import {
  answerLabels,
  newId,
  safeMediaUrl,
} from "./interactive-activity-editor";

export function Field({
  label,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  multiline?: boolean;
}) {
  return (
    <label className="studio-field">
      <span>{label}</span>
      {multiline ? (
        <textarea
          aria-label={label}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
        />
      ) : (
        <input aria-label={label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      )}
      {value&&formatChemistryText(value)!==value&&<span className="chemical-input-preview" aria-label="Aperçu de la notation chimique"><MathText text={value}/></span>}
    </label>
  );
}
export function NumberField({
  label,
  value,
  onChange,
  min,
}: {
  label: string;
  value?: number;
  onChange: (value: number | undefined) => void;
  min?: number;
}) {
  return (
    <label className="studio-field">
      <span>{label}</span>
      <input
        type="number"
        step="any"
        min={min}
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : e.target.valueAsNumber)
        }
      />
    </label>
  );
}
export function SelectField({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="studio-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {children}
      </select>
    </label>
  );
}
function Lines({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <Field
      label={`${label} (une par ligne)`}
      multiline
      value={value?.join("\n")}
      onChange={(v) => onChange(v ? v.split("\n") : [])}
    />
  );
}
function Check({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value?: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="studio-check">
      <input
        type="checkbox"
        checked={!!value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

export function BlockPreview({
  block: b,
  pkg,
  measurementAnswers,
  onMeasurementAnswer,
  printMode=false,
}: {
  printMode?: boolean;
  block: Block;
  pkg: Package;
  measurementAnswers?: Record<string,string>;
  onMeasurementAnswer?: (id:string,value:string)=>void;
}) {
  const [localMeasurement,setLocalMeasurement]=useState('');
  const [previewHelp,setPreviewHelp]=useState(0);
  const measurementPreview=measurementAnswers?.[b.id]??localMeasurement;
  const setMeasurementPreview=(value:string)=>onMeasurementAnswer?onMeasurementAnswer(b.id,value):setLocalMeasurement(value);
  const target=(b.type==='simulation'||b.type==='animation')?b.measurementTargetBlockId:undefined;
  const chemicalAnswers=useRef({value:measurementPreview,table:measurementAnswers?.[target??'']??''});
  chemicalAnswers.current={value:measurementPreview,table:measurementAnswers?.[target??'']??''};
  const actInChemicalLab=async(action:WorldAction)=>{
    if(b.type!=='simulation'&&b.type!=='animation')return;
    const value=chemicalLabAction(b.resourceConfig!,chemicalAnswers.current.value,action);
    const table=target?mergeChemicalMeasurements(value,chemicalAnswers.current.table,b.id):'';
    chemicalAnswers.current={value,table};setMeasurementPreview(value);
    if(target)onMeasurementAnswer?.(target,table);
  };
  let tableRows:Record<string,string>[]=[];
  if(b.type==='measurement_table'&&!b.resourceMeasurementTable){
    try{const saved=JSON.parse(measurementPreview);if(Array.isArray(saved)&&saved.length)tableRows=saved;}catch{}
    if(!tableRows.length)tableRows=b.initialRows??Array.from({length:b.minRows??3},()=>({}));
  }
  const asset =
    "assetId" in b
      ? pkg.interactive.assets.find((a) => a.id === b.assetId)
      : undefined;
  const url = safeMediaUrl(asset?.url);
  const dopplerTrack = dopplerYouTubeTrack(url);
  const graphTable=b.type==='graph_task'?pkg.interactive.blocks.find(row=>row.id===b.sourceTableBlockId):undefined;
  return (
    <div
      className={`studio-student-block type-${b.type}${b.type === "text" ? ` variant-${b.variant ?? "paragraph"}` : ""}`}
    >
      {b.title && <h3><MathText text={b.title} /></h3>}
      {b.type === "molecule" && <MoleculeActivity reference={b.molecule} interactive={!printMode&&b.interactive!==false}/>}
      {b.type === "section" && (
        <>
          <h2><MathText text={b.heading} /></h2>
          <p><MathText text={b.description} /></p>
        </>
      )}
      {(b.type === "text" || b.type === "lab_instruction") && (
        <>
          <PedagogicalText text={b.content} tableLabel="Tableau de l’énoncé"/>
          {b.type==='text'&&b.progressAnalogy&&<ProgressAnalogy model={b.progressAnalogy} staticMode={printMode}/>}
          {b.type==='text'&&<DocumentMolecules items={b.illustrations}/>}
          {b.type === "lab_instruction" &&
            b.safety?.map((s, i) => (
              <p className="studio-safety" key={i}>
                ⚠ <MathText text={s} />
              </p>
            ))}
        </>
      )}
      {b.type === "image" && (
        <>
          {url ? (
            <img src={url} alt={b.caption || asset?.name || "Illustration"} />
          ) : (
            <div className="studio-media-placeholder">Image à renseigner</div>
          )}
          <p className="studio-caption"><MathText text={b.caption} /></p>
        </>
      )}
      {b.type === "document" && (
        <>
          <div className="studio-document">
            ▤ {asset?.name || "Document"}
            {url && (
              <a href={url} target="_blank" rel="noreferrer">
                Ouvrir le document ↗
              </a>
            )}
          </div>
          {url && /\.pdf(?:[?#]|$)|^data:application\/pdf/i.test(url) && (
            <iframe
              title={asset?.name || "Document PDF"}
              src={url}
              sandbox=""
              loading="lazy"
            />
          )}
          <p><MathText text={b.caption} /></p>
        </>
      )}
      {b.type === "video" && (
        <>
          {url ? (
            <video controls preload="metadata" src={url} />
          ) : (
            <div className="studio-media-placeholder">
              Vidéo à renseigner (MP4 / WebM)
            </div>
          )}
          <p><MathText text={b.caption} /></p>
        </>
      )}
      {(b.type === "simulation" || b.type === "animation") && (
        <>
          <p><MathText text={b.prompt} /></p>
          {b.resourceId===chemicalLaboratoryId ? <ChemicalLaboratory key={JSON.stringify(b.resourceConfig)} config={b.resourceConfig!} value={measurementPreview} disabled={printMode} onAction={actInChemicalLab}/> : b.resourceId===measurementEngineId&&printMode ? <MeasurementAndTitrationEngine config={b.resourceConfig!} value={measurementPreview} staticMode/> : b.resourceId===measurementEngineId ? <MeasurementEnginePreview key={JSON.stringify(b.resourceConfig)} config={b.resourceConfig!} value={measurementAnswers?.[b.id]} table={measurementAnswers?.[b.measurementTargetBlockId??'']} sourceId={b.id} onAnswers={onMeasurementAnswer?((value,table)=>{onMeasurementAnswer(b.id,value);onMeasurementAnswer(b.measurementTargetBlockId!,table);}):undefined}/> : dopplerTrack ? (
            <DopplerYouTube key={dopplerTrack.videoId} track={dopplerTrack} />
          ) : asset && resourceForAsset(asset)?.parameterSchema ? (
            b.lockAfterCompletion ? <ValidatedManipulation resource={resourceForAsset(asset)!} config={b.resourceConfig} value={measurementPreview} disabled={false} onValidate={async value=>setMeasurementPreview(value)}/> : b.measurementTargetBlockId&&isExperiment(b.resourceId??'')&&onMeasurementAnswer ? <ExperimentFrame resource={resourceForAsset(asset)!} config={b.resourceConfig} externalTable preview measurements={sourceMeasurements(measurementAnswers?.[b.measurementTargetBlockId]??'',b.id)} onEvent={async event=>{const target=b.measurementTargetBlockId!;const next=applyMeasurementEvent(measurementAnswers?.[target]??'',b.id,b.resourceId!,b.resourceConfig??{},event);onMeasurementAnswer(target,next);return sourceMeasurements(next,b.id);}}/> : <ResourceFrame resource={resourceForAsset(asset)!} config={b.resourceConfig}/>
          ) : url ? (
            <iframe
              title={b.title || b.type}
              src={url}
              sandbox="allow-scripts"
              loading="lazy"
            />
          ) : (
            <div className="studio-media-placeholder">
              ◉ {b.type === "simulation" ? "Simulation" : "Animation"} à
              connecter
              <br />
              <small>
                Le phénomène est décrit ; aucun moteur n’est encore associé.
              </small>
            </div>
          )}
          {!!b.controls?.length && (
            <small>
              Paramètres prévus — le pilotage est assuré dans la ressource
              interactive.
            </small>
          )}
          {b.controls?.map((c) => (
            <label className="studio-slider" key={c.id}>
              <MathText text={c.label} /> {c.unit && <> (<MathText text={c.unit} />)</>}
              <input
                type="range"
                aria-label={c.label}
                min={c.min ?? 0}
                max={c.max ?? 100}
                step={c.step ?? 1}
                defaultValue={c.min ?? 0}
                disabled
              />
            </label>
          ))}
        </>
      )}
      {b.type === 'measurement_table' && b.calibrationSeries && <CalibrationTable block={b} concentrationValue={measurementAnswers?.[b.concentrationSource?.blockId??""]} value={measurementPreview} volumeValue={measurementAnswers?.[b.volumeSourceBlockId??'']} mode={measurementMode(b)} disabled={false} onChange={setMeasurementPreview}/>}
      {b.type === "measurement_table" && b.resourceMeasurementTable && !b.calibrationSeries && <ResourceMeasurementAnswer kind={b.measurementTableKind} acquisitionOnly={b.acquisitionOnly} value={measurementPreview} disabled={false} onChange={setMeasurementPreview}/>}
      {b.type === "measurement_table" && !b.resourceMeasurementTable && (
        <>
          <p><MathText text={b.prompt} /></p>
          <div className="studio-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  {b.columns.map((c) => (
                    <th key={c.id}>
                      <MathText text={c.label} />
                      {c.unit && <small><MathText text={c.unit} /></small>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.slice(0,10).map((row,i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      {b.columns.map((c) => (
                        <td key={c.id}>
                          <input
                            aria-label={`${c.label} ligne ${i + 1}`}
                            type={c.input === "number" ? "number" : "text"}
                            step={c.input==='number'?'any':undefined}
                            value={row[c.id]??''}
                            disabled={printMode||b.readOnlyColumnIds?.includes(c.id)}
                            onChange={e=>setMeasurementPreview(JSON.stringify(tableRows.map((r,index)=>index===i?{...r,[c.id]:e.target.value}:r)))}
                            placeholder="…"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {tableRows.length > 10 && (
            <small>Aperçu limité aux 10 premières lignes.</small>
          )}
        </>
      )}
      {b.type==='graph_task'&&b.graphyo&&<><p><MathText text={b.prompt}/></p><GraphyoActivity key={b.id} config={b.graphyo} value={measurementPreview} readOnly={printMode} disabled={printMode} onChange={setMeasurementPreview}/></>}
      {b.type==='graph_task'&&!b.graphyo&&b.graphConfig&&<><p>{b.prompt}</p><InteractiveGraph key={b.id} config={b.graphConfig} sourcePoints={graphSourcePoints(b,pkg.interactive.blocks,measurementAnswers??{})} value={measurementPreview} onChange={setMeasurementPreview}/></>}
      {b.type==='graph_task'&&!b.graphyo&&!b.graphConfig&&graphTable?.type==='measurement_table'&&graphTable.calibrationSeries&&<CalibrationGraph block={b} value={measurementPreview} rows={calibrationRows(graphTable,measurementAnswers?.[graphTable.id]??'',measurementMode(graphTable),measurementAnswers?.[graphTable.concentrationSource?.blockId??''])} disabled={false} onChange={setMeasurementPreview}/>}
      {b.type === "graph_task" && !b.graphyo && !b.graphConfig && !(graphTable?.type==='measurement_table'&&graphTable.calibrationSeries) && (
        <>
          <p><MathText text={b.prompt} /></p>
          <svg
            className="studio-graph"
            viewBox="0 0 560 200"
            role="img"
            aria-label="Repère à compléter"
          >
            <defs>
              <pattern
                id={`grid-${b.id}`}
                width="35"
                height="30"
                patternUnits="userSpaceOnUse"
              >
                <path d="M 35 0 L 0 0 0 30" fill="none" stroke="#dbe7ef" />
              </pattern>
            </defs>
            <rect
              x="45"
              y="10"
              width="490"
              height="150"
              fill={`url(#grid-${b.id})`}
            />
            <path d="M45 10 V160 H535" stroke="#6b859d" fill="none" />
            <text x="480" y="190">
              {b.xColumnId || "Abscisse"}
            </text>
            <text x="5" y="15">
              {b.yColumnId || "Ordonnée"}
            </text>
          </svg>
          <small>
            Aperçu du repère. Dans le lecteur élève, les points proviennent du carnet de mesures et l’ajustement est lancé par l’élève.
          </small>
        </>
      )}
      {b.type === "question" && (
        <>
          <p className="studio-prompt">
            <MathText text={b.prompt} />
            {b.required && <span aria-label="obligatoire"> *</span>}
          </p>
          {b.diagramLabeling?<ColorimetricTitrationDiagram config={b.diagramLabeling} value={measurementPreview} onChange={setMeasurementPreview} staticMode={printMode}/>:b.progressTable?<ReactionProgressTable config={b.progressTable} value={measurementPreview} onChange={setMeasurementPreview} staticMode={printMode}/>:b.responseFields?<StructuredFields config={b.responseFields} value={measurementPreview} onChange={setMeasurementPreview} staticMode={printMode}/>:b.resourceMeasurementTable ? <ResourceMeasurementAnswer kind={b.measurementTableKind} value={measurementPreview} disabled={false} onChange={setMeasurementPreview}/> : b.answerType === "mcq" ? (
            b.options?.map((o) => (
              <label className="studio-answer" key={o.id}>
                <input type={b.selectionMode === "single" ? "radio" : "checkbox"} name={`answer-${b.id}`} />
                <MathText text={o.text} />
              </label>
            ))
          ) : b.answerType === "true_false" ? (
            <div className="studio-row">
              {["Vrai", "Faux"].map((v) => (
                <label className="studio-answer" key={v}>
                  <input type="radio" name={`answer-${b.id}`} />
                  {v}
                </label>
              ))}
            </div>
          ) : b.answerType === "open" ? (
            <textarea
              aria-label="Réponse élève"
              placeholder="Développez votre réponse…"
              value={b.protocolOrder ? measurementPreview : undefined}
              onChange={b.protocolOrder ? e=>setMeasurementPreview(e.target.value) : undefined}
              rows={3}
            />
          ) : (
            <div className="studio-row">
              <input
                aria-label="Réponse élève"
                type={b.answerType === "numeric" ? "number" : "text"}
                placeholder={
                  b.answerType === "formula"
                    ? "Votre formule ou calcul…"
                    : "Votre réponse…"
                }
              />
              {b.answerType === "numeric" && <span>{b.answerUnit ?? b.correction?.unit}</span>}
            </div>
          )}
          {b.progressiveHints?.length && !printMode && <div>
            {b.progressiveHints.slice(0,previewHelp).map((hint,i)=><div key={i}><p><strong>Aide {i+1} : </strong><MathText text={hint}/></p>{b.choiceHints?.[i]&&<ChoiceHint config={b.choiceHints[i]} number={i+1}/>}</div>)}
            {previewHelp<b.progressiveHints.length&&<button onClick={()=>setPreviewHelp(n=>n+1)}>Afficher l’aide suivante</button>}
            {b.protocolOrder&&previewHelp>=b.progressiveHints.length&&<ProtocolOrderGame config={b.protocolOrder} onUse={setMeasurementPreview}/>}
          </div>}
          {b.hint && (
            <details>
              <summary>Un coup de pouce</summary>
              <p><MathText text={b.hint} /></p>
            </details>
          )}
          {!!b.progressiveHints?.length&&!b.choiceHints?.length&&!printMode&&<details><summary>Coups de pouce</summary>{b.progressiveHints.map((hint,i)=><details key={i}><summary>Aide {i+1}</summary><p><MathText text={hint}/></p></details>)}</details>}
        </>
      )}
    </div>
  );
}

function QuestionEditor({
  b,
  update,
}: {
  b: InteractiveQuestionBlock;
  update: (b: Block) => void;
}) {
  const correction = (
    patch: Partial<NonNullable<InteractiveQuestionBlock["correction"]>>,
  ) => update({ ...b, correction: { ...b.correction, ...patch } });
  function changeType(answerType: InteractiveAnswerType) {
    const next: InteractiveQuestionBlock = {
      ...b,
      answerType,
      correctionMode: "teacher",
      correction: { explanation: b.correction?.explanation },
    };
    delete next.options;
    if (answerType === "mcq")
      next.options = [
        { id: newId("option"), text: "Proposition 1", correct: true },
        { id: newId("option"), text: "Proposition 2" },
      ];
    if (answerType === "true_false")
      next.correction = { ...next.correction, acceptedAnswers: ["true"] };
    update(next);
  }
  return (
    <>
      <Field
        label="Énoncé"
        multiline
        value={b.prompt}
        onChange={(prompt) => update({ ...b, prompt })}
      />
      <small>
        Changer le type conserve l’énoncé et la correction officielle, et
        réinitialise la correction automatique.
      </small>
      <div className="studio-fields">
        <SelectField
          label="Type de réponse"
          value={b.answerType}
          onChange={(v) => changeType(v as InteractiveAnswerType)}
        >
          {Object.entries(answerLabels).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Correction"
          value={b.correctionMode}
          onChange={(v) =>
            update({
              ...b,
              correctionMode: v as InteractiveQuestionBlock["correctionMode"],
            })
          }
        >
          <option value="teacher">Professeur</option>
          {b.answerType !== "open" && (
            <option value="automatic">Automatique</option>
          )}
          <option value="hybrid">Hybride</option>
        </SelectField>
      </div>
      {b.answerType === "mcq" && (
        <fieldset className="studio-options">
          <legend>Propositions · cochez les bonnes réponses</legend>
          {b.options?.map((o, i) => (
            <div className="studio-row" key={o.id}>
              <input
                aria-label={`Bonne réponse ${i + 1}`}
                type="checkbox"
                checked={!!o.correct}
                onChange={(e) =>
                  update({
                    ...b,
                    options: b.options?.map((x) =>
                      x.id === o.id ? { ...x, correct: e.target.checked } : x,
                    ),
                  })
                }
              />
              <Field
                label={`Proposition ${i + 1}`}
                value={o.text}
                onChange={(text) =>
                  update({
                    ...b,
                    options: b.options?.map((x) =>
                      x.id === o.id ? { ...x, text } : x,
                    ),
                  })
                }
              />
              <button
                title="Supprimer la proposition"
                disabled={(b.options?.length ?? 0) <= 2}
                onClick={() =>
                  update({
                    ...b,
                    options: b.options?.filter((x) => x.id !== o.id),
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              update({
                ...b,
                options: [
                  ...(b.options ?? []),
                  { id: newId("option"), text: "Nouvelle proposition" },
                ],
              })
            }
          >
            + Proposition
          </button>
        </fieldset>
      )}
      {b.answerType === "true_false" && (
        <SelectField
          label="Bonne réponse"
          value={b.correction?.acceptedAnswers?.[0] || ""}
          onChange={(v) => correction({ acceptedAnswers: [v] })}
        >
          <option value="">À définir</option>
          <option value="true">Vrai</option>
          <option value="false">Faux</option>
        </SelectField>
      )}
      {b.answerType === "numeric" && (
        <div className="studio-fields">
          <NumberField
            label="Valeur attendue"
            value={b.correction?.numericAnswer}
            onChange={(numericAnswer) => correction({ numericAnswer })}
          />
          <NumberField
            label="Tolérance absolue"
            value={b.correction?.tolerance}
            min={0}
            onChange={(tolerance) => correction({ tolerance })}
          />
          <Field
            label="Unité"
            value={b.answerUnit ?? b.correction?.unit}
            onChange={(unit) => update({...b,answerUnit:unit,correction:{...b.correction,unit}})}
          />
        </div>
      )}
      {(b.answerType === "short_text" || b.answerType === "formula") && (
        <Lines
          label="Réponses acceptées"
          value={b.correction?.acceptedAnswers}
          onChange={(acceptedAnswers) => correction({ acceptedAnswers })}
        />
      )}
      <Field
        label="Correction élève (après restitution)"
        multiline
        value={b.studentCorrection ?? b.correction?.explanation}
        onChange={(studentCorrection) => update({...b,studentCorrection})}
      />
      <Lines label="Aides progressives (une par ligne, facultatives)" value={b.progressiveHints} onChange={progressiveHints=>update({...b,progressiveHints:progressiveHints.length?progressiveHints:undefined})}/>
      <DocumentMoleculesEditor correction items={b.correctionIllustrations} onChange={correctionIllustrations=>update({...b,correctionIllustrations})}/>
      <Field label="Guide professeur (jamais restitué à l’élève)" multiline value={b.teacherGuidance} onChange={teacherGuidance=>update({...b,teacherGuidance})}/>
      <Lines
        label="Mots-clés de correction"
        value={b.correction?.keywords}
        onChange={(keywords) => correction({ keywords })}
      />
      <Field
        label="Aide pour l’élève"
        multiline
        value={b.hint}
        onChange={(hint) => update({ ...b, hint })}
      />
      <div className="studio-row">
        <Check
          label="Réponse obligatoire"
          value={b.required}
          onChange={(required) => update({ ...b, required })}
        />
        <Check
          label="Autoriser une nouvelle réponse"
          value={b.allowRevision}
          onChange={(allowRevision) => update({ ...b, allowRevision })}
        />
        <NumberField
          label="Poids"
          value={b.weight}
          min={0.01}
          onChange={(weight) => update({ ...b, weight })}
        />
      </div>
    </>
  );
}

export function BlockEditor({
  block: b,
  pkg,
  catalog,
  update,
  updateAsset,
}: {
  block: Block;
  pkg: Package;
  catalog: CatalogCompetency[];
  update: (b: Block) => void;
  updateAsset: (asset: InteractiveAssetRef) => void;
}) {
  const asset =
    "assetId" in b
      ? pkg.interactive.assets.find((a) => a.id === b.assetId)
      : undefined;
  function changeAsset(patch: Partial<InteractiveAssetRef>) {
    if (
      b.type !== "image" &&
      b.type !== "document" &&
      b.type !== "video" &&
      b.type !== "animation" &&
      b.type !== "simulation"
    )
      return;
    const id = b.assetId || newId("asset");
    // Studio owns the combined transaction when creating a new resource.
    updateAsset({
      id,
      kind: b.type as InteractiveAssetRef["kind"],
      name: asset?.name || "Nouvelle ressource",
      ...asset,
      ...patch,
    });
  }
  return (
    <div className="studio-block-editor">
      <div className="studio-editor-heading">
        Modifier ce bloc <code>{b.id}</code>
      </div>
      {(b.type==="simulation"||b.type==="animation")&&asset&&resourceForAsset(asset)?.parameterSchema&&<ResourceParameters resource={resourceForAsset(asset)!} config={b.resourceConfig??resourceForAsset(asset)!.defaultConfig!} onChange={resourceConfig=>update({...b,resourceId:asset.resourceId,resourceVersion:asset.resourceVersion,resourceConfig})}/>}
      <Field
        label="Titre du bloc (facultatif)"
        value={b.title}
        onChange={(title) => update({ ...b, title })}
      />
      {b.type === "molecule" && <><MoleculeReferenceFields allowLearnerChoices={b.interactive!==false} value={b.molecule} onChange={molecule=>update({...b,molecule})}/><label><input type="checkbox" checked={b.interactive!==false} onChange={e=>update({...b,interactive:e.target.checked})}/>Vue interactive pour l’élève</label></>}
      {b.type === "section" && (
        <>
          <Field
            label="Titre de la partie"
            value={b.heading}
            onChange={(heading) => update({ ...b, heading })}
          />
          <Field
            label="Introduction de la partie"
            multiline
            value={b.description}
            onChange={(description) => update({ ...b, description })}
          />
        </>
      )}
      {(b.type === "text" || b.type === "lab_instruction") && (
        <Field
          label="Contenu"
          multiline
          value={b.content}
          onChange={(content) => update({ ...b, content })}
        />
      )}
      {b.type === "text" && (
        <DocumentMoleculesEditor items={b.illustrations} onChange={illustrations=>update({...b,illustrations})}/>
      )}
      {b.type === "text" && (
        <SelectField
          label="Présentation"
          value={b.variant ?? "paragraph"}
          onChange={(v) => update({ ...b, variant: v as typeof b.variant })}
        >
          <option value="paragraph">Paragraphe</option>
          <option value="instruction">Consigne</option>
          <option value="method">Méthode</option>
          <option value="warning">Attention</option>
        </SelectField>
      )}
      {b.type === "lab_instruction" && (
        <Lines
          label="Consignes de sécurité"
          value={b.safety}
          onChange={(safety) => update({ ...b, safety })}
        />
      )}
      {b.type === "question" && <QuestionEditor b={b} update={update} />}
      {["image", "document", "video", "animation", "simulation"].includes(
        b.type,
      ) && (
        <fieldset>
          <legend>Ressource</legend>
          <Field
            label="Nom de la ressource"
            value={asset?.name}
            onChange={(name) => changeAsset({ name })}
          />
          <Field
            label="Adresse de la ressource (HTTPS ou chemin /…)"
            value={asset?.url}
            onChange={(url) => changeAsset({ url })}
          />
          {asset?.url && !safeMediaUrl(asset.url) && (
            <p className="studio-warning">
              Adresse non affichable : utilisez HTTPS ou un chemin commençant
              par /.
            </p>
          )}
          {(b.type === "image" ||
            b.type === "document" ||
            b.type === "video") && (
            <Field
              label="Légende"
              value={b.caption}
              onChange={(caption) => update({ ...b, caption })}
            />
          )}
          <small>
            Animations et simulations : URL d’une page interactive isolée.
            Vidéos : lien direct MP4 / WebM.
          </small>
        </fieldset>
      )}
      {(b.type === "animation" || b.type === "simulation") && (
        <>
          <Field
            label="Phénomène / consigne"
            multiline
            value={b.prompt}
            onChange={(prompt) => update({ ...b, prompt })}
          />
          <Lines
            label="Relations attendues"
            value={b.expectedRelationships}
            onChange={(expectedRelationships) =>
              update({ ...b, expectedRelationships })
            }
          />
          <fieldset>
            <legend>Paramètres manipulables</legend>
            {b.controls?.map((c, i) => (
              <div className="studio-repeated" key={c.id}>
                <Field
                  label={`Paramètre ${i + 1}`}
                  value={c.label}
                  onChange={(label) =>
                    update({
                      ...b,
                      controls: b.controls?.map((x) =>
                        x.id === c.id ? { ...x, label } : x,
                      ),
                    })
                  }
                />
                <div className="studio-fields">
                  {(["min", "max", "step"] as const).map((key) => (
                    <NumberField
                      key={key}
                      label={
                        { min: "Minimum", max: "Maximum", step: "Pas" }[key]
                      }
                      value={c[key]}
                      onChange={(v) =>
                        update({
                          ...b,
                          controls: b.controls?.map((x) =>
                            x.id === c.id ? { ...x, [key]: v } : x,
                          ),
                        })
                      }
                    />
                  ))}
                  <Field
                    label="Unité du paramètre"
                    value={c.unit}
                    onChange={(unit) =>
                      update({
                        ...b,
                        controls: b.controls?.map((x) =>
                          x.id === c.id ? { ...x, unit } : x,
                        ),
                      })
                    }
                  />
                </div>
                <button
                  onClick={() =>
                    update({
                      ...b,
                      controls: b.controls?.filter((x) => x.id !== c.id),
                    })
                  }
                >
                  Supprimer ce paramètre
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                update({
                  ...b,
                  controls: [
                    ...(b.controls ?? []),
                    {
                      id: newId("control"),
                      label: "Paramètre",
                      min: 0,
                      max: 100,
                      step: 1,
                    },
                  ],
                })
              }
            >
              + Paramètre
            </button>
          </fieldset>
        </>
      )}
      {b.type === "measurement_table" && (
        <>
          {b.calibrationSeries&&<SelectField label="Origine des mesures de ce tableau" value={b.dataSource??'manual'} onChange={v=>update({...b,dataSource:v as 'manual'|'simulation'|'provided'})}><option value="manual">Saisies par les élèves (manipulation réelle)</option><option value="simulation">Acquises dans l’animation</option><option value="provided">Fournies par le professeur</option></SelectField>}
          {b.calibrationSeries&&<fieldset><legend>Jeu de données fourni par le professeur</legend>{b.calibrationSeries.map((row,i)=><NumberField key={row.solutionId} label={`Absorbance ${row.solutionId}`} value={row.absorbance} onChange={absorbance=>update({...b,calibrationSeries:b.calibrationSeries!.map((r,j)=>i===j?{...r,absorbance}:r)})}/>)}</fieldset>}
          <Field
            label="Consigne du tableau"
            multiline
            value={b.prompt}
            onChange={(prompt) => update({ ...b, prompt })}
          />
          <div className="studio-fields">
            <NumberField
              label="Lignes minimum"
              value={b.minRows}
              min={1}
              onChange={(minRows) => update({ ...b, minRows })}
            />
            <NumberField
              label="Lignes maximum"
              value={b.maxRows}
              min={1}
              onChange={(maxRows) => update({ ...b, maxRows })}
            />
          </div>
          <fieldset>
            <legend>Colonnes</legend>
            {b.columns.map((c, i) => {
              const col = (patch: Partial<typeof c>) =>
                update({
                  ...b,
                  columns: b.columns.map((x) =>
                    x.id === c.id ? { ...x, ...patch } : x,
                  ),
                });
              return (
                <div className="studio-repeated" key={c.id}>
                  <div className="studio-fields">
                    <Field
                      label={`Colonne ${i + 1}`}
                      value={c.label}
                      onChange={(label) => col({ label })}
                    />
                    <Field
                      label="Unité de mesure"
                      value={c.unit}
                      onChange={(unit) => col({ unit })}
                    />
                    <SelectField
                      label="Saisie"
                      value={c.input}
                      onChange={(v) => col({ input: v as typeof c.input })}
                    >
                      <option value="number">Nombre</option>
                      <option value="text">Texte</option>
                    </SelectField>
                  </div>
                  <div className="studio-fields">
                    {(["min", "max", "tolerance"] as const).map((key) => (
                      <NumberField
                        key={key}
                        label={
                          {
                            min: "Valeur min. attendue",
                            max: "Valeur max. attendue",
                            tolerance: "Tolérance",
                          }[key]
                        }
                        value={c.expected?.[key]}
                        onChange={(v) =>
                          col({ expected: { ...c.expected, [key]: v } })
                        }
                      />
                    ))}
                  </div>
                  <button
                    disabled={b.columns.length <= 1}
                    onClick={() =>
                      update({
                        ...b,
                        columns: b.columns.filter((x) => x.id !== c.id),
                      })
                    }
                  >
                    Supprimer cette colonne
                  </button>
                </div>
              );
            })}
            <button
              onClick={() =>
                update({
                  ...b,
                  columns: [
                    ...b.columns,
                    {
                      id: newId("col"),
                      label: "Nouvelle mesure",
                      input: "number",
                    },
                  ],
                })
              }
            >
              + Colonne
            </button>
          </fieldset>
        </>
      )}
      {b.type === "graph_task" && b.graphyo && <><Field label="Consigne Graphyo" multiline value={b.prompt} onChange={prompt=>update({...b,prompt})}/><GraphyoConfigEditor config={b.graphyo} onChange={graphyo=>update({...b,graphyo})}/></>}
      {b.type === "graph_task" && !b.graphyo && (
        <>
          <button type="button" onClick={()=>update({...b,graphConfig:undefined,sourceTableBlockId:undefined,graphyo:{version:1,presentation:'classic',initialRows:5,columns:[{name:'Abscisse',symbol:'x',unit:'1'},{name:'Ordonnée',symbol:'y',unit:'1'}]}})}>Utiliser Graphyo</button>
          <label><input type="checkbox" checked={!!b.graphConfig} onChange={e=>update({...b,graphConfig:e.target.checked?defaultGraphConfig():undefined})}/>Utiliser le module Graphique interactif</label>
          {b.graphConfig&&<><Field label="Consigne du graphique" multiline value={b.prompt} onChange={prompt=>update({...b,prompt})}/><GraphConfigEditor config={b.graphConfig} onChange={graphConfig=>update({...b,graphConfig})}/></>}
          <div hidden={!!b.graphConfig}>
          {(["allowRegression","showRegression","showEquation","showR2"] as const).map(key=><label key={key}><input type="checkbox" checked={b[key]??(key!=="showR2")} onChange={e=>update({...b,[key]:e.target.checked})}/>{{allowRegression:"Autoriser l’ajustement après action de l’élève",showRegression:"Afficher la droite après ajustement",showEquation:"Afficher l’équation après ajustement",showR2:"Afficher R² après ajustement"}[key]}</label>)}
          <Field
            label="Consigne du graphique"
            multiline
            value={b.prompt}
            onChange={(prompt) => update({ ...b, prompt })}
          />
          </div>
          <SelectField
            label="Tableau source"
            value={b.sourceTableBlockId ?? ""}
            onChange={(v) =>
              update({
                ...b,
                sourceTableBlockId: v || undefined,
                xColumnId: undefined,
                yColumnId: undefined,
              })
            }
          >
            <option value="">Sans tableau lié</option>
            {pkg.interactive.blocks
              .filter((x) => x.type === "measurement_table")
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title || t.id}
                </option>
              ))}
          </SelectField>
          <div className="studio-fields">
            {(["xColumnId", "yColumnId"] as const).map((key) => {
              const t = pkg.interactive.blocks.find(
                (x) => x.id === b.sourceTableBlockId,
              );
              return (
                <SelectField
                  key={key}
                  label={key === "xColumnId" ? "Abscisse" : "Ordonnée"}
                  value={b[key] ?? ""}
                  onChange={(v) => update({ ...b, [key]: v || undefined })}
                >
                  <option value="">À définir</option>
                  {t?.type === "measurement_table" &&
                    t.columns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label} {c.unit}
                      </option>
                    ))}
                </SelectField>
              );
            })}
          </div>
        </>
      )}
      {(b.type === "measurement_table" || b.type === "graph_task") && (
        <SelectField
          label="Correction"
          value={b.correctionMode || "teacher"}
          onChange={(v) =>
            update({
              ...b,
              correctionMode: v as "teacher" | "automatic" | "hybrid",
            })
          }
        >
          <option value="teacher">Professeur</option>
          <option value="hybrid">Hybride</option>
          <option value="automatic">Automatique</option>
        </SelectField>
      )}
      {(b.type === "question" ||
        b.type === "measurement_table" ||
        b.type === "graph_task") && (
        <fieldset>
          <legend>Compétences associées · 0 à 4</legend>
          {b.type === "question" && <p>Ces tags désignent les compétences travaillées : cette question fournit une preuve. L’évaluation est regroupée dans un checkpoint.</p>}
          <div className="studio-competency-picker">
            {catalog
              .filter(
                (c) =>
                  c.schoolLevel === pkg.activity.schoolLevel ||
                  b.competencyIds?.includes(c.id),
              )
              .map((c) => (
                <Check
                  key={c.id}
                  label={`${c.code} · ${c.label}`}
                  value={b.competencyIds?.includes(c.id)}
                  disabled={
                    !pkg.activity.competencyIds.includes(c.id) &&
                    pkg.activity.competencyIds.length >= 4
                  }
                  onChange={(checked) =>
                    update({
                      ...b,
                      competencyIds: checked
                        ? [...(b.competencyIds ?? []), c.id]
                        : b.competencyIds?.filter((id) => id !== c.id),
                    })
                  }
                />
              ))}
          </div>
          <small>
            3 à 4 compétences réellement évaluées maximum pour toute l’activité.
          </small>
        </fieldset>
      )}
      {['question','measurement_table','graph_task'].includes(b.type)&&<label>Photo, image ou téléphone<select value={b.allowAttachments===undefined?'auto':b.allowAttachments?'yes':'no'} onChange={e=>update({...b,allowAttachments:e.target.value==='auto'?undefined:e.target.value==='yes'})}><option value="auto">Selon le type de réponse</option><option value="no">Désactivés</option><option value="yes">Autorisés explicitement</option></select></label>}
      <Field
        label="Note privée du professeur"
        multiline
        value={b.teacherNote}
        onChange={(teacherNote) => update({ ...b, teacherNote })}
      />
    </div>
  );
}


